import { faCirclePlay as farCirclePlay, faHeart as farHeart } from '@fortawesome/free-regular-svg-icons';
import {
  faArrowUp,
  faArrowUpRightFromSquare,
  faChevronRight,
  faCircleXmark,
  faHeart,
} from '@fortawesome/free-solid-svg-icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import cn from 'clsx';
import { Button, Modal } from 'flowbite-react';
import { usePostHog } from 'posthog-js/react';
import { JSX, KeyboardEventHandler, ReactElement, ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Link, useLoaderData, useSearchParams } from 'react-router-dom';

import { mutations } from 'src/api/mutations';
import { queries } from 'src/api/queries';
import { ChatBubble, ChatBubbleProps } from 'src/components/chat-bubble';
import { FaIconLite } from 'src/components/fa-icon-lite';
import { Player, VideoJS } from 'src/components/video-js';
import {
  isUseCaseAbandonedCart,
  mapFontSizes,
  parseHtmlStringToReact,
  removeNulls,
  sleep,
  useParsedHtmlString,
} from 'src/utils';

import { usePosthogAttributionDataFromLoader } from './hooks';
import { LocalizedStrings, VideosContext } from './shared';

import MaverickLogo from 'src/assets/img/maverick-wordmark-new-large.svg';
import classes from './videos.module.css';

function HeartVideoIcon(props: { didHeart: boolean; onHeart: () => void }): ReactElement {
  const { didHeart, onHeart } = props;
  return (
    <button
      type='button'
      className='cursor-pointer rounded-full px-2 py-1 text-2xl text-gray-500 hover:bg-gray-200 hover:text-red-500'
      onClick={onHeart}
    >
      {didHeart || <FaIconLite icon={farHeart} />}
      {didHeart && <FaIconLite icon={faHeart} className='text-red-600' />}
    </button>
  );
}

function VideoPosterMessage(props: {
  onClick: () => void;
  onHeart: () => void;
  didHeart: boolean;
  animateCirclePlay: boolean;
  posterImage: string | undefined;
}): ReactElement {
  const { onClick, onHeart, didHeart, animateCirclePlay, posterImage } = props;
  return (
    <>
      <div onClick={onClick} className='relative cursor-pointer'>
        <img alt='video thumbnail' src={posterImage} className='block h-auto w-full object-cover' />
        <div className='absolute inset-y-1/2 flex w-full items-center justify-center'>
          <FaIconLite icon={farCirclePlay} size='3x' className={'text-white'} beat={animateCirclePlay} />
        </div>
      </div>
      <div className='absolute inset-y-1/2 -right-14 flex items-center justify-center'>
        <HeartVideoIcon didHeart={didHeart} onHeart={onHeart}></HeartVideoIcon>
      </div>
    </>
  );
}

function ProductImagesMessage(props: {
  href: string;
  productImageUrl1: string | undefined;
  productImageUrl2: string | undefined;
  onClick: () => void;
}): ReactElement {
  const { href, productImageUrl1, productImageUrl2, onClick } = props;
  const columnsClassName = productImageUrl1 && productImageUrl2 ? 'columns-2' : 'columns-1';
  return (
    <>
      <div className='relative cursor-pointer'>
        <div className={columnsClassName}>
          {productImageUrl1 && (
            <div>
              <a
                target='blank'
                href={href}
                onClick={onClick}
                className='underline decoration-sky-500 decoration-solid hover:decoration-blue-500'
              >
                <img alt='product image 1' src={productImageUrl1} className='block h-auto w-full object-cover' />
              </a>
            </div>
          )}
          {productImageUrl2 && (
            <div>
              <a
                target='blank'
                href={href}
                onClick={onClick}
                className='underline decoration-sky-500 decoration-solid hover:decoration-blue-500'
              >
                <img alt='product image 2' src={productImageUrl2} className='block h-auto w-full object-cover' />
              </a>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function CtaMessage(props: { message: ReactNode; href: string; onClick: () => void }): ReactElement {
  const { message, href, onClick } = props;
  return (
    <a
      target='blank'
      href={href}
      onClick={onClick}
      className='underline decoration-sky-500 decoration-solid hover:decoration-blue-500'
    >
      <span>
        {message} <FaIconLite icon={faArrowUpRightFromSquare} />
      </span>
    </a>
  );
}

type Message = Omit<ChatBubbleProps, 'children'> &
  (
    | {
        messageType: 'string';
        message: string | JSX.Element | JSX.Element[];
      }
    | {
        messageType: 'cta';
        message: string;
        href: string;
      }
  );

export function Videos(): ReactElement {
  const {
    object,
    useCaseInfo,
    customerFirstName,
    videoPageInfo,
    storeInfo,
    ctaUrl,
    hyperlinkVideoUrl,
    phone,
    email,
    deliveryMethod,
    couponCode,
  } = useLoaderData() as VideosContext;
  useLayoutEffect(() => {
    // Activate CSS outside react when this component loads, see :global in videos.module.css
    document.documentElement.classList.add('video-landing-page');
    return () => {
      document.documentElement.classList.remove('video-landing-page');
    };
  }, []);

  const posthog = usePostHog();
  const posthogData = usePosthogAttributionDataFromLoader();

  const messagesDiv = useRef<HTMLDivElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const userDidSendMessage = useRef(false);
  const didPromptUserToReply = useRef<{ heart: boolean; videoEnd: boolean }>({
    heart: false,
    videoEnd: false,
  });
  const videoPlayer = useRef<Player | null>(null);

  const [textAreaValue, setTextAreaValue] = useState('');
  const [openVideoModal, setOpenVideoModal] = useState(false);
  const [didHeart, setDidHeart] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [showTypingIndicator, setShowTypingIndicator] = useState(false);
  const [animateCTA, setAnimateCTA] = useState(false);
  const [animateCirclePlay, setAnimateCirclePlay] = useState(true);
  const [isAbandonedCart] = useState(isUseCaseAbandonedCart(useCaseInfo));

  const createFeedback = useMutation(mutations.createFeedback);

  const [searchParams] = useSearchParams();
  const { data: productImages } = useQuery({
    ...queries.shopifyCartProductImages(
      storeInfo.ownerOrganization,
      storeInfo.keyName,
      searchParams.get('shopify_checkout_id'),
      searchParams.get('shopify_checkout_token'),
      [searchParams.get('shopify_product_id_0'), searchParams.get('shopify_product_id_1')].filter(removeNulls),
    ),
    placeholderData: { productImages: [] },
    enabled: isUseCaseAbandonedCart(useCaseInfo),
  });

  const messageText = useParsedHtmlString(
    (videoPageInfo.messageText ?? '')
      .replace('{ coupon_code }', '{coupon_code}')
      .replace('{coupon_code}', couponCode ?? ''),
  );
  const supportingText = useParsedHtmlString(
    videoPageInfo.supportingText
      ?.replace('{ coupon_code }', '{coupon_code}')
      .replace('{coupon_code}', couponCode ?? ''),
  );
  const buttonText = useParsedHtmlString(videoPageInfo.buttonText);

  const strings = useCaseInfo.language === 'SPANISH' ? LocalizedStrings.es : LocalizedStrings.en;

  const scrollToBottom = () => {
    setTimeout(() => {
      if (messagesDiv.current) {
        messagesDiv.current.lastElementChild?.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const postVideoWatchText = videoPageInfo.postVideoWatchText
    ? parseHtmlStringToReact(videoPageInfo.postVideoWatchText)
    : strings.videoSendMessage();
  const postHeartText = videoPageInfo.postHeartText
    ? parseHtmlStringToReact(videoPageInfo.postHeartText)
    : strings.heartSendMessage();
  const postReplyText = videoPageInfo.postReplyText
    ? parseHtmlStringToReact(videoPageInfo.postReplyText)
    : strings.thanksForReply();

  const promptUserToReply = async (source: 'heart' | 'videoEnd') => {
    if (isAbandonedCart) {
      setAnimateCirclePlay(false);
      setAnimateCTA(true);
      return;
    }
    if (!videoPageInfo.showFeedbackBox || didPromptUserToReply.current[source] || userDidSendMessage.current) {
      return;
    }
    didPromptUserToReply.current[source] = true;

    await sleep(200);
    setShowTypingIndicator(true);
    scrollToBottom();
    await sleep(1000);
    setShowTypingIndicator(false);
    setMessages((m) => [
      ...m,
      {
        type: 'received',
        messageType: 'string',
        message: source === 'heart' ? postHeartText : postVideoWatchText,
      },
    ]);
    textAreaRef.current?.focus();
    scrollToBottom();
  };

  const onHeart = async () => {
    setDidHeart((old) => !old);
    scrollToBottom();
    await promptUserToReply('heart');
  };

  const appendUserSentMessages = async (message: string) => {
    setMessages((m) => [...m, { type: 'sent', messageType: 'string', message: message }]);
    scrollToBottom();

    if (userDidSendMessage.current) {
      return;
    }
    userDidSendMessage.current = true;
    await sleep(200);
    setShowTypingIndicator(true);
    scrollToBottom();
    await sleep(1000);
    setShowTypingIndicator(false);
    setMessages((m) => [
      ...m,
      {
        type: 'received',
        messageType: 'string',
        message: postReplyText,
      },
    ]);
    scrollToBottom();
  };

  const sendUserMessage = async () => {
    if (textAreaValue === '') {
      return;
    }
    await appendUserSentMessages(textAreaValue);
    setTextAreaValue('');

    await createFeedback.mutateAsync([
      storeInfo.ownerOrganization,
      hyperlinkVideoUrl,
      textAreaValue,
      email,
      phone,
      deliveryMethod ?? null,
    ]);
  };

  const onTextAreaKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendUserMessage();
    }
  };

  const useInViewReturns = useInView({
    /* Optional options */
    threshold: 0.9,
  });

  const topCtaRef = useInViewReturns.ref;
  const topCtaInView = useInViewReturns.inView;

  return (
    <section className='mx-auto h-full min-h-dvh bg-zinc-50'>
      <div className='mx-auto block h-full min-h-dvh w-screen max-w-2xl bg-white'>
        <div
          className={cn(
            'fixed top-0 z-10 flex h-24 w-full max-w-2xl flex-col justify-center p-1 text-center',
            classes.message_header,
          )}
        >
          {storeInfo.logo && (
            <Link to={videoPageInfo.ctaUrl} className='h-16'>
              <img
                alt='store logo'
                className={cn(
                  { 'mix-blend-multiply': !storeInfo.logo.endsWith('.png') },
                  'm-auto max-h-full max-w-full',
                )}
                src={storeInfo.logo}
              />
            </Link>
          )}
        </div>

        <div
          className={cn(
            classes.message_body,
            `${mapFontSizes(videoPageInfo.merchantFontSize ?? 'BASE')} flex flex-col py-28`,
          )}
          ref={messagesDiv}
        >
          <ChatBubble type='received'>{strings.hello(customerFirstName)}</ChatBubble>
          {Boolean(messageText) && <ChatBubble type='received'>{messageText}</ChatBubble>}

          {isAbandonedCart && Boolean(buttonText) && (ctaUrl ?? videoPageInfo.ctaUrl) && (
            <div className='mb-4 pl-3' ref={topCtaRef}>
              <Button
                className='h-16 max-w-[255px] border-2 !no-underline '
                style={{ backgroundColor: videoPageInfo.buttonColor }}
                href={ctaUrl ?? videoPageInfo.ctaUrl}
                onClick={() => posthog.capture('ctaClicked', posthogData)}
                theme={{ inner: { base: 'w-full my-auto' } }}
              >
                <div className='flex w-full justify-between text-xl'>
                  <p style={{ color: videoPageInfo.buttonTextColor }}>{buttonText}</p>
                  <FaIconLite
                    size='xl'
                    icon={faChevronRight}
                    style={{ color: videoPageInfo.buttonTextColor }}
                    beat={animateCTA}
                  />
                </div>
              </Button>
            </div>
          )}
          <ChatBubble type='received' className='w-full'>
            <VideoPosterMessage
              onClick={() => {
                setOpenVideoModal(true);
              }}
              onHeart={() => void onHeart()}
              didHeart={didHeart}
              animateCirclePlay={animateCirclePlay}
              posterImage={videoPageInfo.posterImgUrl ?? ''}
            />
          </ChatBubble>
          {Boolean(supportingText) && <ChatBubble type='received'>{supportingText}</ChatBubble>}

          {productImages !== undefined &&
            Boolean(productImages.productImages.length) &&
            (ctaUrl ?? videoPageInfo.ctaUrl) && (
              <ChatBubble type='received'>
                <ProductImagesMessage
                  productImageUrl1={productImages.productImages[0]}
                  productImageUrl2={productImages.productImages.length > 1 ? productImages.productImages[1] : undefined}
                  href={ctaUrl ?? videoPageInfo.ctaUrl}
                  onClick={() => posthog.capture('ctaClicked', posthogData)}
                />
              </ChatBubble>
            )}
          {isAbandonedCart && Boolean(buttonText) && (ctaUrl ?? videoPageInfo.ctaUrl) && (
            <div className={`${topCtaInView && 'invisible'} mb-4 pl-3`}>
              <Button
                className='h-16 max-w-[255px] border-2 !no-underline'
                style={{ backgroundColor: videoPageInfo.buttonColor }}
                href={ctaUrl ?? videoPageInfo.ctaUrl}
                onClick={() => posthog.capture('ctaClicked', posthogData)}
                theme={{ inner: { base: 'w-full my-auto' } }}
              >
                <div className='flex w-full justify-between text-xl'>
                  <p style={{ color: videoPageInfo.buttonTextColor }}>{buttonText}</p>
                  <FaIconLite
                    size='xl'
                    icon={faChevronRight}
                    style={{ color: videoPageInfo.buttonTextColor }}
                    beat={animateCTA}
                  />
                </div>
              </Button>
            </div>
          )}
          {!isAbandonedCart && Boolean(buttonText) && (ctaUrl ?? videoPageInfo.ctaUrl) && (
            <ChatBubble type='received'>
              <CtaMessage
                message={buttonText}
                href={ctaUrl ?? videoPageInfo.ctaUrl}
                onClick={() => posthog.capture('ctaClicked', posthogData)}
              />
            </ChatBubble>
          )}
          {messages.map((message, index) => (
            <ChatBubble type={message.type} className={message.className} key={index}>
              {message.messageType === 'string' && message.message}
              {message.messageType === 'cta' && (
                <CtaMessage
                  message={message.message}
                  href={message.href}
                  onClick={() => posthog.capture('ctaClicked', posthogData)}
                />
              )}
            </ChatBubble>
          ))}
          {showTypingIndicator && (
            <ChatBubble type='received'>
              <div className='flex h-5 items-center justify-center'>
                <div className={classes.typingIndicatorBubbleDot}></div>
                <div className={classes.typingIndicatorBubbleDot}></div>
                <div className={classes.typingIndicatorBubbleDot}></div>
              </div>
            </ChatBubble>
          )}
        </div>

        {videoPageInfo.showFeedbackBox && (
          <div
            className={cn(
              'fixed flex w-full max-w-2xl items-center bg-gray-50 px-1 py-2 text-center',
              videoPageInfo.showPlgLogo ? 'bottom-8' : 'bottom-0',
            )}
          >
            <HeartVideoIcon didHeart={didHeart} onHeart={() => void onHeart()}></HeartVideoIcon>
            <div className='relative mr-1 w-full'>
              <textarea
                id='chat'
                rows={1}
                ref={textAreaRef}
                className={`${mapFontSizes(videoPageInfo.merchantFontSize ?? 'BASE')} mx-1 block w-full resize-none rounded-full border border-gray-300 bg-white p-2.5 px-5 text-gray-900 focus:border-blue-500 focus:ring-blue-500`}
                placeholder={strings.replyPlaceholder(useCaseInfo.speakerName ?? '')}
                value={textAreaValue}
                onChange={(e) => {
                  setTextAreaValue(e.target.value);
                }}
                onKeyDown={onTextAreaKeyDown}
              />
              {textAreaValue !== '' && (
                <button
                  className='absolute inset-y-0 right-0 my-auto h-8 cursor-pointer justify-center rounded-full bg-blue-600 px-2.5 py-1 text-white hover:bg-blue-800'
                  onClick={() => void sendUserMessage()}
                >
                  <FaIconLite icon={faArrowUp} />
                </button>
              )}
            </div>
          </div>
        )}
        {videoPageInfo.showPlgLogo && (
          <div className='fixed bottom-0 w-full max-w-2xl items-center bg-gray-50 pb-2 pt-0 text-center'>
            <Link
              to='/'
              reloadDocument
              target='_blank'
              onClick={() => posthog.capture('poweredByMaverickClicked', posthogData)}
            >
              <span className='text-sm text-gray-500'>
                <span className='align-middle'>Powered By</span>
                <MaverickLogo className='inline h-4 pl-1' />
              </span>
            </Link>
          </div>
        )}
      </div>
      <Modal
        dismissible
        popup
        position='center'
        size='lg'
        show={openVideoModal}
        onClose={() => {
          if (videoPlayer.current && !videoPlayer.current.paused()) {
            videoPlayer.current.pause();
          }
          setOpenVideoModal(false);
          scrollToBottom();
          setTimeout(() => void promptUserToReply('videoEnd'), 500);
        }}
      >
        <div
          className='absolute left-0 top-0 z-20 cursor-pointer p-4'
          onClick={() => {
            if (videoPlayer.current && !videoPlayer.current.paused()) {
              videoPlayer.current.pause();
            }
            setOpenVideoModal(false);
            scrollToBottom();
            setTimeout(() => void promptUserToReply('videoEnd'), 500);
          }}
        >
          <FaIconLite icon={faCircleXmark} className='cursor-pointer justify-center rounded-full text-4xl text-white' />
        </div>
        <VideoJS
          options={{
            autoplay: true,
            controls: true,
            controlBar: {
              fullscreenToggle: false,
              pictureInPictureToggle: false,
            },
            disablePictureInPicture: true,
            enableSmoothSeeking: true,
            experimentalSvgIcons: true,
            fill: true,
            html5: {
              nativeControlsForTouch: true,
              nativeTextTracks: true,
              preloadTextTracks: false,
            },
            playsinline: false,
            poster: videoPageInfo.posterImgUrl ?? '',
            preload: 'auto',
            sources: [{ src: object.videoUrl, type: 'video/mp4' }],
            textTrackSettings: false,
            userActions: {
              doubleClick: false,
            },
          }}
          className='h-[95dvh]'
          onReady={(player) => {
            player.on('play', function () {
              posthog.capture('customerVideo_videojsplay', posthogData);
              setAnimateCTA(true);
            });
            player.on('pause', function () {
              posthog.capture('customerVideo_videojspause', posthogData);
            });
            player.on('canplay', function () {
              posthog.capture('customerVideo_videojscanplay', posthogData);
            });
            player.on('canplaythrough', function () {
              posthog.capture('customerVideo_videojscanplaythrough', posthogData);
            });
            player.on('ended', function () {
              posthog.capture('customerVideo_videojsended', posthogData);
              setOpenVideoModal(false);
              scrollToBottom();
              setTimeout(() => void promptUserToReply('videoEnd'), 500);
            });
            player.on('loadeddata', function () {
              posthog.capture('customerVideo_videojsloadeddata', posthogData);
            });
            videoPlayer.current = player;
          }}
        />
      </Modal>
    </section>
  );
}
